import { useCallback, useEffect, useState } from 'react';

import {
  useBlockNoteEditor,
  useComponentsContext,
  useDictionary,
  useEditorDOMElement,
  useEditorState,
  useExtension,
} from '@blocknote/react';

import { CouplespaceEditLinkMenuItems } from './CouplespaceEditLinkMenuItems.web';
import { useEditorLinkPicker } from './editor-link-picker-context.web';

function LinkIcon() {
  return (
    <svg fill="none" height="16" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="16">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

function canCreateLink(editor: ReturnType<typeof useBlockNoteEditor>): boolean {
  if (!editor.isEditable) {
    return false;
  }
  const selection = editor.getSelection()?.blocks ?? [editor.getTextCursorPosition().block];
  return selection.some((block) => block.content !== undefined);
}

export function CouplespaceCreateLinkButton() {
  const editor = useBlockNoteEditor();
  const Components = useComponentsContext();
  const dictionary = useDictionary();
  const domElement = useEditorDOMElement();
  const formattingToolbar = useExtension('formattingToolbar');
  const { registerOpen } = useEditorLinkPicker();
  const [open, setOpen] = useState(false);

  const linkState = useEditorState({
    editor,
    selector: ({ editor: currentEditor }) => {
      if (!currentEditor || !canCreateLink(currentEditor)) {
        return undefined;
      }
      return {
        url: currentEditor.getSelectedLinkUrl() ?? '',
        text: currentEditor.getSelectedText() ?? '',
        range: {
          from: currentEditor.prosemirrorState.selection.from,
          to: currentEditor.prosemirrorState.selection.to,
        },
      };
    },
  });

  const close = useCallback(() => {
    setOpen(false);
    formattingToolbar?.store?.setState(false);
  }, [formattingToolbar]);

  const openPicker = useCallback(() => {
    if (!linkState) {
      return;
    }
    setOpen(true);
  }, [linkState]);

  useEffect(() => {
    return registerOpen(openPicker);
  }, [openPicker, registerOpen]);

  useEffect(() => {
    setOpen(false);
  }, [linkState]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        openPicker();
      }
    };
    domElement?.addEventListener('keydown', onKeyDown);
    return () => {
      domElement?.removeEventListener('keydown', onKeyDown);
    };
  }, [domElement, openPicker]);

  if (!Components || !linkState) {
    return null;
  }

  const Popover = Components.Generic.Popover;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger>
        <Components.FormattingToolbar.Button
          className="bn-button"
          data-test="createLink"
          icon={<LinkIcon />}
          label={dictionary.formatting_toolbar.link.tooltip}
          mainTooltip={dictionary.formatting_toolbar.link.tooltip}
          secondaryTooltip={dictionary.formatting_toolbar.link.secondary_tooltip}
          onClick={() => setOpen((value) => !value)}
        />
      </Popover.Trigger>
      <Popover.Content className="bn-popover-content bn-form-popover" variant="form-popover">
        <CouplespaceEditLinkMenuItems
          range={linkState.range}
          text={linkState.text}
          url={linkState.url}
          showTextField={false}
          setToolbarOpen={(value) => formattingToolbar?.store?.setState(value)}
          onClose={close}
        />
      </Popover.Content>
    </Popover.Root>
  );
}
